import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { agruparReservas } from "@/lib/reservasUtils";
import { CalendarDays, UserPlus, Wrench, ArrowRight, Ticket, ClipboardList, Package } from "lucide-react";

const WEEKDAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

function fmtFechaCorta(yyyymmdd) {
    const [y, m, d] = yyyymmdd.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((dt - today) / 86400000);
    if (diff === 0) return "Hoy";
    if (diff === 1) return "Mañana";
    return `${WEEKDAYS[dt.getDay()]} ${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}`;
}

const ACCIONES = [
    { to: "/reservar", icon: CalendarDays, label: "Reservar sala", desc: "Bloques de 30 minutos" },
    { to: "/cargar-visita", icon: UserPlus, label: "Cargar visita", desc: "Anunciar visitantes" },
    { to: "/reportar-incidencia", icon: Wrench, label: "Reportar incidencia", desc: "Algo no funciona" },
];

export default function Inicio() {
    const { user } = useAuth();
    const ue = user?.usuario_edificio;
    const primerNombre = (user?.nombre || "").split(" ")[0] || "";

    const [reservas, setReservas] = useState(null);
    const [visitas, setVisitas] = useState(null);
    const [incidencias, setIncidencias] = useState(null);
    const [paquetes, setPaquetes] = useState(null);
    const [unidad, setUnidad] = useState("");

    useEffect(() => {
        api.get("/reservas/mias").then((r) => setReservas(r.data)).catch(() => setReservas([]));
        api.get("/visitas/mias").then((r) => setVisitas(r.data)).catch(() => setVisitas([]));
        api.get("/incidencias/mias").then((r) => setIncidencias(r.data)).catch(() => setIncidencias([]));
        api.get("/paqueteria").then((r) => setPaquetes(r.data)).catch(() => setPaquetes([]));
        if (ue?.unidad_id) {
            api.get("/unidades").then((r) => {
                const u = r.data.find((x) => x.unidad_id === ue.unidad_id);
                if (u) setUnidad(u.etiqueta);
            }).catch(() => {});
        }
    }, [ue?.unidad_id]);

    const proximoGrupo = agruparReservas(reservas || [])[0];
    const visitasPendientes = (visitas || []).filter((v) => v.estado !== "llego").length;
    const reclamosAbiertos = (incidencias || []).filter((i) => i.estado !== "resuelto").length;
    // Paquetes pendientes propios: asociados al usuario directamente +
    // manuales (sin destinatario_usuario_edif_id) cargados para mi unidad.
    const miUedId = ue?.usuario_edif_id;
    const paquetesPendientes = (paquetes || []).filter((p) => {
        if (p.estado !== "pendiente") return false;
        if (p.destinatario_usuario_edif_id) {
            return miUedId && p.destinatario_usuario_edif_id === miUedId;
        }
        // Manual sin destinatario específico: lo ve toda la unidad
        return true;
    }).length;

    return (
        <div className="max-w-2xl">
            {/* Saludo */}
            <div className="mb-5">
                <h1 className="font-heading text-2xl font-bold tracking-tight">
                    Hola{primerNombre ? `, ${primerNombre}` : ""}
                </h1>
                {unidad && (
                    <p className="text-sm text-muted-foreground mt-0.5">
                        Unidad <span className="font-semibold text-foreground">{unidad}</span>
                    </p>
                )}
            </div>

            {/* Acciones principales */}
            <div className="grid gap-2.5 mb-6">
                {ACCIONES.map((a, idx) => {
                    const Icon = a.icon;
                    return (
                        <Link
                            key={a.to}
                            to={a.to}
                            data-testid={`accion-${idx}`}
                            className="group flex items-center gap-4 p-4 rounded-lg border border-border bg-card hover:border-accent/40 hover:bg-accent/5 transition-all"
                        >
                            <div className={`h-11 w-11 rounded-md grid place-items-center shrink-0 ${
                                idx === 0 ? "bg-primary text-primary-foreground" : "bg-accent/15 text-accent"
                            }`}>
                                <Icon className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-heading font-semibold text-base leading-tight">{a.label}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{a.desc}</p>
                            </div>
                            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
                        </Link>
                    );
                })}
            </div>

            {/* Próximo */}
            <section>
                <h2 className="text-[11px] uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-2 px-1">
                    Próximo
                </h2>
                <Card>
                    <CardContent className="p-0 divide-y divide-border">
                        {/* Reserva */}
                        <Link to="/mis-reservas" className="flex items-center gap-3 px-3 py-3 hover:bg-primary-soft transition-colors" data-testid="proximo-reservas">
                            <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div className="flex-1 min-w-0">
                                {proximoGrupo ? (
                                    <p className="text-sm leading-tight truncate">
                                        <span className="font-semibold">
                                            {fmtFechaCorta(proximoGrupo.fecha)} {proximoGrupo.hora_inicio} a {proximoGrupo.hora_fin}
                                        </span>
                                        <span className="text-muted-foreground">
                                            {proximoGrupo.reservas.length > 1
                                                ? ` · ${proximoGrupo.reservas.length} bloques`
                                                : " · próxima reserva"}
                                        </span>
                                    </p>
                                ) : (
                                    <p className="text-sm text-muted-foreground">No tenés reservas próximas.</p>
                                )}
                            </div>
                            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        </Link>

                        {/* Visitas */}
                        <Link to="/mis-visitas" className="flex items-center gap-3 px-3 py-3 hover:bg-primary-soft transition-colors" data-testid="proximo-visitas">
                            <Ticket className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div className="flex-1 min-w-0">
                                {visitasPendientes > 0 ? (
                                    <p className="text-sm leading-tight">
                                        <span className="font-semibold">{visitasPendientes}</span>
                                        <span className="text-muted-foreground"> visita{visitasPendientes === 1 ? "" : "s"} pendiente{visitasPendientes === 1 ? "" : "s"} de llegar</span>
                                    </p>
                                ) : (
                                    <p className="text-sm text-muted-foreground">No tenés visitas pendientes.</p>
                                )}
                            </div>
                            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        </Link>

                        {/* Reclamos */}
                        <Link to="/mis-incidencias" className="flex items-center gap-3 px-3 py-3 hover:bg-primary-soft transition-colors" data-testid="proximo-reclamos">
                            <ClipboardList className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div className="flex-1 min-w-0">
                                {reclamosAbiertos > 0 ? (
                                    <p className="text-sm leading-tight">
                                        <span className="font-semibold">{reclamosAbiertos}</span>
                                        <span className="text-muted-foreground"> reclamo{reclamosAbiertos === 1 ? "" : "s"} abierto{reclamosAbiertos === 1 ? "" : "s"}</span>
                                    </p>
                                ) : (
                                    <p className="text-sm text-muted-foreground">No tenés reclamos abiertos.</p>
                                )}
                            </div>
                            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        </Link>

                        {/* Paquetes */}
                        <Link to="/mis-paquetes" className="flex items-center gap-3 px-3 py-3 hover:bg-primary-soft transition-colors" data-testid="proximo-paquetes">
                            <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div className="flex-1 min-w-0">
                                {paquetesPendientes > 0 ? (
                                    <p className="text-sm leading-tight">
                                        <span className="text-muted-foreground">Tenés </span>
                                        <span className="font-semibold">{paquetesPendientes}</span>
                                        <span className="text-muted-foreground"> paquete{paquetesPendientes === 1 ? "" : "s"} pendiente{paquetesPendientes === 1 ? "" : "s"} de retiro.</span>
                                    </p>
                                ) : (
                                    <p className="text-sm text-muted-foreground">No tenés paquetes pendientes.</p>
                                )}
                            </div>
                            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        </Link>
                    </CardContent>
                </Card>
            </section>
        </div>
    );
}
